import React, { useEffect, useState } from 'react';
import { produce } from 'immer';
import { useLocation, useNavigate } from 'react-router-dom';

import {
  getVariableEditPathname,
  hasAnyValidationConfigured,
} from '../../utils';
import { FACTOR_LABELS, SUBCHECK_LABELS } from './const/guardrails.labels';
import { ComplexityTag, StatusFactor, StatusTagConfig } from './tag';
import { VariableType } from '@/constants/variable';
import { VariableSettingsAction } from '@/features/variable/constants';
import { useUpdateVariable } from '@/hooks';
import { FactorMetadata, VariableInfo } from '@/lib/App';

export const slackLink =
  'https://grab.enterprise.slack.com/archives/C09UMEL61FV';
export const slackLabel = '#pmk-team';

const CONFIG: StatusTagConfig = {
  tagLabel: 'Guardrail',
  passLabel: 'Implemented',
  failLabel: 'Guardrails Not Implemented',
  docLink:
    'https://docs.google.com/document/d/13b1QoOh5b-wrloFUi2h6vCfPen97OSQupbL2_sFZwAo/edit?tab=t.wkhmi135l02q',
  slackLink: slackLink,
  slackLabel: slackLabel,
  footerNote:
    'Guardrail requirements ensure every variable has clear ownership, documentation, and rollout safety checks in place.',
};

const buildFactors = (
  variable: VariableInfo,
  onFix: (tab?: string) => void,
): StatusFactor[] => {
  const isBoolean = variable?.type?.toLowerCase() === VariableType.BOOLEAN;

  const getFixAction = (tab: string) => () => onFix(tab);

  const getFactorSubChecks = (
    factor: string,
    factorMetadata: FactorMetadata,
  ) => {
    switch (factor) {
      case 'owners':
        return [
          {
            label: SUBCHECK_LABELS.engineeringOwner,
            description:
              'Identifies the engineer responsible for maintaining and supporting this variable in production.',
            passed: !factorMetadata?.violated,
            action: getFixAction(VariableSettingsAction.EDIT),
          },
          {
            label: SUBCHECK_LABELS.productOwner,
            description:
              'Identifies the product manager accountable for the business intent and lifecycle of this variable.',
            passed: !factorMetadata?.violated,
            action: getFixAction(VariableSettingsAction.EDIT),
          },
        ];
      case 'documentation':
        return [
          {
            label: SUBCHECK_LABELS.documentationLink,
            description:
              'A link to the design doc, PRD, or Confluence page explaining the purpose, usage, and expected behaviour of this variable.',
            passed: !factorMetadata?.violated,
            action: getFixAction(VariableSettingsAction.EDIT),
          },
        ];
      case 'mandatoryFacet':
        return [
          {
            label: SUBCHECK_LABELS.mandatoryFacets,
            description:
              'Enabling mandatory facets will trigger auto-migration of your existing rollouts. A new rollout will be created for Publisher review and approval. Rollout targets without a ServiceType (svc) or City constraint will block the save.',
            passed: !factorMetadata?.violated,
            action: getFixAction(VariableSettingsAction.EDIT),
            waivable: true,
          },
        ];
      case 'validation': {
        const validationPassed =
          !factorMetadata?.violated || hasAnyValidationConfigured(variable);
        if (isBoolean) {
          return [
            {
              label: SUBCHECK_LABELS.validation,
              description:
                'Validation ensures that the variable value meets specific criteria, such as format, length, or pattern.',
              passed: true,
              action: getFixAction(VariableSettingsAction.VALIDATE),
              waivable: true,
              skipped:
                factorMetadata?.violated && factorMetadata?.['exceptionReason'],
            },
          ];
        }

        return [
          {
            label: SUBCHECK_LABELS.enabledValidation,
            description:
              'Validation ensures that the variable value meets specific criteria, such as format, length, or pattern.',
            passed: validationPassed,
            action: getFixAction(VariableSettingsAction.VALIDATE),
            waivable: true,
          },
        ];
      }
      default:
        return;
    }
  };

  return Object.keys(variable?.complexity?.guardrails ?? {}).map(
    (factor: string) => {
      const factorMetadata = variable?.complexity?.guardrails?.[factor];
      const resolvedKey = factorMetadata?.key || factor;

      const passed =
        resolvedKey === 'validation'
          ? !factorMetadata?.violated || hasAnyValidationConfigured(variable)
          : !factorMetadata?.violated;

      return {
        key: resolvedKey,
        label:
          FACTOR_LABELS[resolvedKey] ??
          FACTOR_LABELS[factor] ??
          factorMetadata?.label,
        passed,
        subChecks: getFactorSubChecks(resolvedKey, factorMetadata),
      };
    },
  );
};

interface Props {
  variable: VariableInfo;
  onVariableSaved?: () => void;
}

const getJustificationsFromVariable = (
  variable: VariableInfo,
): Record<string, string> =>
  Object.entries(variable?.complexity?.guardrails ?? {}).reduce(
    (acc, [key, metadata]) => {
      if (metadata?.exceptionReason) {
        acc[key] = metadata.exceptionReason;
      }
      return acc;
    },
    {} as Record<string, string>,
  );

const GuardrailsTag = ({ variable, onVariableSaved }: Props) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { pathname } = location;
  const { mutate } = useUpdateVariable();
  const [justifications, setJustifications] = useState<Record<string, string>>(
    () => getJustificationsFromVariable(variable),
  );

  useEffect(() => {
    setJustifications(getJustificationsFromVariable(variable));
  }, [variable]);

  const handleJustify = (key: string, text: string) => {
    setJustifications((prev) => ({ ...prev, [key]: text }));

    const updated = produce(variable, (draft) => {
      if (draft.complexity?.guardrails?.[key]) {
        draft.complexity.guardrails[key].exceptionReason = text;
      }
    });

    mutate(updated, {
      onSuccess: () => {
        onVariableSaved?.();
      },
      onError: () => {
        onVariableSaved?.();
      },
    });
  };

  const openVariableSettings = (tab?: string) => {
    const nextPathname = getVariableEditPathname(
      pathname,
      variable?.name ?? '',
      tab,
    );
    navigate(
      {
        ...location,
        pathname: nextPathname,
      },
      { replace: true },
    );
  };

  const factors = buildFactors(variable, openVariableSettings);

  if (!factors.length) return null;

  return (
    <ComplexityTag
      dataTestId="guardrails-tag"
      config={CONFIG}
      factors={factors}
      justifications={justifications}
      onJustify={handleJustify}
    />
  );
};

export default GuardrailsTag;
